import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "p-8" }
const _hoisted_2 = { class: "text-gray-500 text-sm mb-4" }
const _hoisted_3 = {
  key: 0,
  class: "mb-4 p-4 bg-red-50 border border-red-200 rounded-xl"
}
const _hoisted_4 = { class: "flex items-start" }
const _hoisted_5 = { class: "text-sm text-red-700 mt-1" }
const _hoisted_6 = { class: "flex gap-2 mb-2" }
const _hoisted_7 = ["onUpdate:modelValue", "disabled", "onInput", "onKeydown", "onPaste"]
const _hoisted_8 = {
  key: 1,
  class: "text-red-600 text-sm mb-4"
}
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { key: 2 }
const _hoisted_13 = { class: "text-center text-sm mt-4 text-gray-600" }
const _hoisted_14 = ["disabled"]

import { ref, onUnmounted, computed, watch } from "vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import * as yup from "yup";
import ResetHeader from "./ResetHeader.vue";
import { validateResetTokenAction } from "../../actions/validateResetToken.action";
import { useMutation } from "@tanstack/vue-query";
import { forgotPasswordAction } from "../../actions/forgotPassword.actions";
import { useToast } from "vue-toastification";

const COOLDOWN_DURATION = 60; // 60 segundos entre cada reenvío

// Sistema de seguridad
const MAX_RESEND_ATTEMPTS = 3; // Máximo 3 reenvíos
const BLOCK_DURATION = 300; // 5 minutos de bloqueo (300 segundos)


export default /*@__PURE__*/_defineComponent({
  __name: 'StepCodeVerification',
  props: {
    email: {},
    isLoading: { type: Boolean }
  },
  emits: ["verify-code", "resend-code", "resetPassword","sendCode","security-blocked"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const toast = useToast();

const localToken = ref(["", "", "", "", "", ""]);
const errorMessage = ref<string>("");

const codeSchema = toTypedSchema(
  yup.object({
    code: yup
      .string()
      .required("Ingresa el código de 6 dígitos")
      .length(6, "El código debe tener 6 dígitos")
      .matches(/^\d{6}$/, "Solo se permiten números"),
  })
);

const { setFieldValue, handleSubmit, errors } = useForm({
  validationSchema: codeSchema,
  initialValues: { code: "" },
});

watch(
  localToken,
  (arr) => {
    setFieldValue("code", arr.join(""), false);
  },
  { deep: true }
);

// Estado del cooldown
const cooldownTime = ref(0);
const cooldownInterval = ref<number | null>(null);
const resendCount = ref(0);
const isSecurityBlocked = ref(false);
const blockTime = ref(0);
const blockInterval = ref<number | null>(null);
const isPending = computed(() => rawIsPending.value ?? false);
const { mutate: sendCode, isPending: rawIsPending } = useMutation({
  mutationFn: (token: string) => validateResetTokenAction(token),
  onSuccess: (data) => {
    const isValid = data?.data?.data?.valid === true;
    
    if (isValid) {
      errorMessage.value = '';
      emit('verify-code', localToken.value.join(''));
    } else {
      localToken.value = ['', '', '', '', '', ''];
      toast.error('Código inválido. Por favor, verifica e intenta nuevamente.');
      setTimeout(() => {
        const inputs = document.querySelectorAll<HTMLInputElement>("input");
        inputs[0]?.focus();
      }, 0);
    }
  },
  onError: (err: any) => {
    errorMessage.value = err?.message || 'Error al verificar el código. Intenta nuevamente.';
    localToken.value = ['', '', '', '', '', ''];
  }
});

function handleInput(index: number) {
  // Solo permitir números
  const value = localToken.value[index];
  if (value && !/^\d$/.test(value)) {
    localToken.value[index] = '';
    return;
  }

  if (index < 5 && localToken.value[index]) {
    const inputs = document.querySelectorAll<HTMLInputElement>("input");
    inputs[index + 1]?.focus();
  }
}

function handleKeyDown(event: KeyboardEvent, index: number) {
  const inputs = document.querySelectorAll<HTMLInputElement>("input");
  
  // Manejar Backspace
  if (event.key === 'Backspace') {
    if (!localToken.value[index] && index > 0) {
      // Si el campo actual está vacío, ir al anterior
      inputs[index - 1]?.focus();
      localToken.value[index - 1] = '';
    } else {
      // Limpiar el campo actual
      localToken.value[index] = '';
    }
  }
  
  // Manejar Delete
  if (event.key === 'Delete') {
    localToken.value[index] = '';
  }
  
  // Manejar flechas
  if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault();
    inputs[index - 1]?.focus();
  }
  
  if (event.key === 'ArrowRight' && index < 5) {
    event.preventDefault();
    inputs[index + 1]?.focus();
  }

  // Manejar Enter para verificar
  if (event.key === "Enter") {
    event.preventDefault();
    onVerify();
  }
}

function handlePaste(event: ClipboardEvent) {
  event.preventDefault();
  
  const pastedData = event.clipboardData?.getData('text') || '';
  const cleanedData = pastedData.replace(/\D/g, ''); // Solo números
  
  if (cleanedData.length === 6) {
    // Si el código pegado tiene exactamente 6 dígitos
    for (let i = 0; i < 6; i++) {
      localToken.value[i] = cleanedData[i];
    }
    
    // Enfocar el último input
    const inputs = document.querySelectorAll<HTMLInputElement>("input");
    inputs[5]?.focus();
    
   
  } else if (cleanedData.length > 0) {
    // Si tiene menos o más de 6 dígitos, llenar lo que se pueda
    const maxLength = Math.min(cleanedData.length, 6);
    for (let i = 0; i < maxLength; i++) {
      localToken.value[i] = cleanedData[i];
    }
    
    const inputs = document.querySelectorAll<HTMLInputElement>("input");
    const nextIndex = Math.min(maxLength, 5);
    inputs[nextIndex]?.focus();
    
    if (cleanedData.length !== 6) {
      toast.warning('El código debe tener 6 dígitos');
    }
  }
}

const onVerify = handleSubmit((values) => {
  if (isSecurityBlocked.value) {
    toast.error(
      `Bloqueado por seguridad. Intenta en ${formatTime(blockTime.value)}`
    );
    return;
  }
  sendCode(values.code);
});

function startCooldown() {
  cooldownTime.value = COOLDOWN_DURATION;
  
  if (cooldownInterval.value) {
    clearInterval(cooldownInterval.value);
  }
  
  cooldownInterval.value = window.setInterval(() => {
    cooldownTime.value--;
    
    if (cooldownTime.value <= 0) {
      stopCooldown();
    }
  }, 1000);
}

function stopCooldown() {
  if (cooldownInterval.value) {
    clearInterval(cooldownInterval.value);
    cooldownInterval.value = null;
  }
  cooldownTime.value = 0;
}

function startSecurityBlock() {
  isSecurityBlocked.value = true;
  blockTime.value = BLOCK_DURATION;
  
  if (blockInterval.value) {
    clearInterval(blockInterval.value);
  }
  
  blockInterval.value = window.setInterval(() => {
    blockTime.value--;
    
    if (blockTime.value <= 0) {
      stopSecurityBlock();
    }
  }, 1000);

  emit('security-blocked', true);
  toast.error(`Demasiados intentos. Bloqueado por ${BLOCK_DURATION / 60} minutos por seguridad.`);
}

function stopSecurityBlock() {
  if (blockInterval.value) {
    clearInterval(blockInterval.value);
    blockInterval.value = null;
  }
  isSecurityBlocked.value = false;
  blockTime.value = 0;
  resendCount.value = 0; // Reiniciar contador al desbloquear

  emit('security-blocked', false);
}

async function ResendCode() {
  // Verificar bloqueo de seguridad
  if (isSecurityBlocked.value) {
    toast.warning(`Bloqueado por seguridad. Espera ${formatTime(blockTime.value)}`);
    return;
  }

  // Verificar cooldown normal
  if (cooldownTime.value > 0) {
    toast.warning(`Espera ${cooldownTime.value}s antes de reenviar el código`);
    return;
  }

  // Incrementar contador de reenvíos
  resendCount.value++;

  // Verificar si se alcanzó el límite
  if (resendCount.value >= MAX_RESEND_ATTEMPTS) {
    startSecurityBlock();
    return;
  }

  try {
    await forgotPasswordAction(props.email);
    const attemptsLeft = MAX_RESEND_ATTEMPTS - resendCount.value;
    toast.success(`Código reenviado.`);
    startCooldown();
    
    // Limpiar inputs
    localToken.value = ['', '', '', '', '', ''];
    setTimeout(() => {
      const inputs = document.querySelectorAll<HTMLInputElement>("input");
      inputs[0]?.focus();
    }, 0);
  } catch {
    toast.error("Error al reenviar el código. Intenta nuevamente.");
  }
}

// Formatear tiempo en minutos y segundos
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, '0')} min`;
  }
  return `${secs}s`;
}

// Computed para el texto del botón de reenvío
const resendButtonText = computed(() => {
  if (isSecurityBlocked.value) {
    return `Bloqueado (${formatTime(blockTime.value)})`;
  }
  if (cooldownTime.value > 0) {
    return `Reenviar en ${cooldownTime.value}s`;
  }
  const attemptsLeft = MAX_RESEND_ATTEMPTS - resendCount.value;
  return `Reenviar código (${attemptsLeft} ${attemptsLeft === 1 ? 'restante' : 'restantes'})`;
});

const canResend = computed(() => {
  return cooldownTime.value === 0 && !isPending.value && !isSecurityBlocked.value;
});

// Limpiar intervalos al desmontar
onUnmounted(() => {
  stopCooldown();
  stopSecurityBlock();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ResetHeader, {
      title: "Nova",
      subtitle: "Innovación de ventas sin límites"
    }),
    _createElementVNode("div", _hoisted_1, [
      _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "text-2xl font-bold text-gray-900 mb-2" }, "Ingresa tu código", -1)),
      _createElementVNode("p", _hoisted_2, [
        _cache[1] || (_cache[1] = _createTextVNode(" Hemos enviado un código de 6 dígitos a ", -1)),
        _createElementVNode("strong", null, _toDisplayString(__props.email), 1)
      ]),
      _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-sm font-semibold text-slate-800 mb-2" }, [
        _createTextVNode(" Código de verificación "),
        _createElementVNode("span", { class: "text-red-500" }, "*")
      ], -1)),
      (isSecurityBlocked.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _cache[3] || (_cache[3] = _createElementVNode("svg", {
                class: "w-5 h-5 text-red-600 mt-0.5 mr-3",
                fill: "currentColor",
                viewBox: "0 0 20 20"
              }, [
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                  "clip-rule": "evenodd"
                })
              ], -1)),
              _createElementVNode("div", null, [
                _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "text-sm font-semibold text-red-800" }, "Cuenta bloqueada temporalmente", -1)),
                _createElementVNode("p", _hoisted_5, " Por seguridad, se bloqueó el reenvío de códigos. Podrás intentar nuevamente en " + _toDisplayString(formatTime(blockTime.value)) + ". ", 1)
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localToken.value, (digit, index) => {
          return _withDirectives((_openBlock(), _createElementBlock("input", {
            key: index,
            "onUpdate:modelValue": ($event: any) => ((localToken.value[index]) = $event),
            maxlength: "1",
            type: "text",
            inputmode: "numeric",
            pattern: "[0-9]",
            disabled: isSecurityBlocked.value,
            class: "w-full text-center text-2xl border-2 border-gray-200 rounded-xl py-3 focus:border-slate-900 focus:outline-none transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed",
            onInput: ($event: any) => (handleInput(index)),
            onKeydown: ($event: any) => (handleKeyDown($event, index)),
            onPaste: ($event: any) => (index === 0 ? handlePaste($event) : null)
          }, null, 40, _hoisted_7)), [
            [_vModelText, localToken.value[index]]
          ])
        }), 128))
      ]),
      (_unref(errors).code)
        ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_unref(errors).code), 1))
        : _createCommentVNode("", true),
      _createElementVNode("button", {
        onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onVerify) && _unref(onVerify)(...args))),
        disabled: isPending.value || localToken.value.join('').length !== 6 || isSecurityBlocked.value,
        class: "w-full bg-slate-900 text-white py-3 rounded-xl font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-slate-800 transition-colors"
      }, [
        (isSecurityBlocked.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_10, "BLOQUEADO"))
          : (isPending.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_11, "Verificando..."))
            : (_openBlock(), _createElementBlock("span", _hoisted_12, "VERIFICAR CÓDIGO"))
      ], 8, _hoisted_9),
      _createElementVNode("div", _hoisted_13, [
        _cache[4] || (_cache[4] = _createTextVNode(" ¿No recibiste el código? ", -1)),
        _createElementVNode("button", {
          onClick: ResendCode,
          disabled: !canResend.value,
          class: _normalizeClass(["font-semibold ml-1 transition-colors", canResend.value 
          ? 'text-slate-700 hover:text-slate-900' 
          : 'text-gray-400 cursor-not-allowed'])
        }, _toDisplayString(resendButtonText.value), 11, _hoisted_14)
      ])
    ])
  ], 64))
}
}

})